import React, { useState, useCallback, useRef, useEffect } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { duration } from './primitives/tokens';
import { Portal } from './primitives/Portal';
import { useFloating } from '../../hooks/useFloating';
import { zIndex as Z } from '../../tokens/zIndex';
import { Icon } from './Icon';
import { staggerItem } from './primitives/motion/presets';
import styles from './Dropdown.module.css';

// ── CVA ──────────────────────────────────────────────────────────────────────

const dropdownItem = cva(styles.item, {
  variants: {
    danger: { true: styles.itemDanger },
  },
});

// ── Types ─────────────────────────────────────────────────────────────────────

type Placement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'left-start'
  | 'left-end'
  | 'right-start'
  | 'right-end';
type Trigger = 'hover' | 'click' | 'contextMenu';

interface MenuItem {
  key: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  danger?: boolean;
  disabled?: boolean;
  type?: 'item' | 'divider' | 'group';
  extra?: React.ReactNode;
  /** Mark this item as selected */
  selected?: boolean;
  /**
   * Custom indicator rendered at the trailing edge when `selected=true`.
   * - Default: `<Icon name="check" size={14} />` (rendered when `selected=true` and prop is omitted)
   * - `null`: disables the indicator even when `selected=true`
   * - ReactNode: replaces the default check icon
   */
  selectedIndicator?: React.ReactNode | null;
  onClick?: () => void;
  /**
   * When present, this item is a submenu parent: it renders a trailing chevron
   * and opens the given content in a side flyout on hover/focus. Clicking the
   * parent does not select/close the menu. The content is arbitrary (e.g. a
   * nested item list, or a searchable picker).
   */
  submenu?: React.ReactNode;
}

interface DropdownProps {
  /** Menu items */
  menu: { items: MenuItem[]; onClick?: (key: string) => void };
  /** How to trigger the dropdown */
  trigger?: Trigger[];
  /** Placement */
  placement?: Placement;
  /** Controlled open */
  open?: boolean;
  /** Open state callback */
  onOpenChange?: (open: boolean) => void;
  /** Disabled */
  disabled?: boolean;
  /** Custom styles merged onto the menu container */
  menuStyle?: React.CSSProperties;
  /** Match menu width to the trigger element width */
  matchTriggerWidth?: boolean;
  /** Override menu position — use when trigger is managed externally (e.g. contextmenu on a wrapper div) */
  menuPosition?: { x: number; y: number };
  /** Custom z-index — use when the menu is opened from inside another floating layer (e.g. `zIndex={zIndex.dropdownInPopover}` for a Dropdown nested in a Popover) */
  zIndex?: number;
  /** Trigger element */
  children: React.ReactElement;
}

function DropdownItem({ item, onSelect }: { item: MenuItem; onSelect: (key: string) => void }) {
  const [subOpen, setSubOpen] = useState(false);
  const subLeaveTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const openSub = useCallback(() => {
    clearTimeout(subLeaveTimer.current);
    setSubOpen(true);
  }, []);
  const closeSub = useCallback(() => {
    subLeaveTimer.current = setTimeout(() => setSubOpen(false), 120);
  }, []);
  useEffect(() => () => clearTimeout(subLeaveTimer.current), []);

  if (item.type === 'divider') {
    return <div className={styles.divider} />;
  }

  // Determine what indicator to show:
  // - selected=true + selectedIndicator undefined → default check icon
  // - selected=true + selectedIndicator=null → no indicator
  // - selected=true + selectedIndicator=ReactNode → custom indicator
  // - selected=false or undefined → no indicator
  let indicator: React.ReactNode = null;
  if (item.selected) {
    if (item.selectedIndicator === undefined) {
      indicator = <Icon name="check" size={14} />;
    } else if (item.selectedIndicator !== null) {
      indicator = item.selectedIndicator;
    }
  }

  const hasSub = item.submenu != null;

  return (
    <div
      className={styles.itemWrap}
      onMouseEnter={hasSub ? openSub : undefined}
      onMouseLeave={hasSub ? closeSub : undefined}
      onFocus={hasSub ? openSub : undefined}
      onBlur={hasSub ? closeSub : undefined}
    >
      <button
        role="menuitem"
        aria-haspopup={hasSub ? 'true' : undefined}
        aria-expanded={hasSub ? subOpen : undefined}
        className={dropdownItem({ danger: item.danger })}
        // A submenu parent never selects/closes the menu — hovering opens the
        // flyout; clicking it just toggles (helps touch/keyboard).
        onClick={() => {
          if (item.disabled) return;
          if (hasSub) { setSubOpen((o) => !o); return; }
          item.onClick?.() ?? onSelect(item.key);
        }}
        disabled={item.disabled}
      >
        {item.icon && <span className={styles.itemIcon}>{item.icon}</span>}
        <span className={styles.itemLabel}>{item.label}</span>
        {item.extra && <span className={styles.itemExtra}>{item.extra}</span>}
        {hasSub && <span className={styles.itemSubChevron}><Icon name="chevron_right" size={14} /></span>}
        {indicator && <span className={styles.itemIndicator}>{indicator}</span>}
      </button>
      {hasSub && subOpen && (
        <div className={styles.submenu} onMouseEnter={openSub} onMouseLeave={closeSub}>
          {item.submenu}
        </div>
      )}
    </div>
  );
}

/**
 * Dropdown — contextual menu triggered by click/hover/right-click.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | menu | { items, onClick? } | — | Menu configuration |
 * | trigger | ('hover' \| 'click' \| 'contextMenu')[] | ['hover'] | Trigger modes |
 * | placement | Placement | 'bottom-start' | Position |
 * | open | boolean | — | Controlled state |
 * | onOpenChange | (open) => void | — | State callback |
 * | disabled | boolean | false | Disable trigger |
 * | zIndex | number | Z.dropdown | z-index layer — override when nesting inside another floating layer |
 *
 * Menu item: `{ key, label, icon?, danger?, disabled?, type?: 'divider', selected?, selectedIndicator?, onClick? }`
 * - `selected?: boolean` — marks the item as selected; renders a trailing indicator
 * - `selectedIndicator?: ReactNode | null` — override the default check icon; `null` disables it
 *
 * @example
 * <Dropdown menu={{ items: [
 *   { key: 'edit', label: 'Edit' },
 *   { key: 'delete', label: 'Delete', danger: true },
 * ]}}>
 *   <Button>Actions</Button>
 * </Dropdown>
 */
export function Dropdown({
  menu,
  trigger = ['hover'],
  placement = 'bottom-start',
  open: controlledOpen,
  onOpenChange,
  disabled = false,
  menuStyle: customMenuStyle,
  matchTriggerWidth = false,
  menuPosition,
  zIndex = Z.dropdown,
  children,
}: DropdownProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const shouldReduce = useReducedMotion();
  // Cursor position captured on right-click; non-null means use it instead of element-relative positioning.
  const [contextPos, setContextPos] = useState<{ x: number; y: number } | null>(null);

  const leaveTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const { triggerRef, floatingRef, position } = useFloating<HTMLElement, HTMLDivElement>({
    placement,
    offset: 4,
    open: isOpen && contextPos === null && !menuPosition,
  });

  // Priority: external menuPosition prop > internal contextPos from right-click > element-relative useFloating position.
  const resolvedPosition = menuPosition
    ? { top: menuPosition.y, left: menuPosition.x }
    : contextPos
    ? { top: contextPos.y, left: contextPos.x }
    : position;

  const setOpen = useCallback(
    (next: boolean) => {
      if (disabled) return;
      if (!next) setContextPos(null);
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange, disabled],
  );

  const show = useCallback(() => {
    clearTimeout(leaveTimer.current);
    setOpen(true);
  }, [setOpen]);

  const hide = useCallback(() => {
    leaveTimer.current = setTimeout(() => setOpen(false), 100);
  }, [setOpen]);

  // Outside click — listens on `pointerdown`, not `mousedown`. Pointer
  // events are the unified primitive across mouse/touch/pen and fire
  // BEFORE mousedown (when mousedown fires at all). React Flow uses
  // pointerdown for its pane-level gestures (drag, selection-box,
  // pan), so listening on pointerdown lets the dropdown dismiss when
  // the user clicks anywhere on the canvas, even surfaces that
  // suppress bubble-mousedown. Consumers that need to keep the
  // dropdown open while clicking inside their content already use
  // `onPointerDown stopPropagation` for the same purpose with respect
  // to RF — this listener honors that block too.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: PointerEvent) => {
      if (triggerRef.current?.contains(e.target as Node)) return;
      if (floatingRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [isOpen, setOpen, triggerRef, floatingRef]);

  useEffect(() => {
    return () => clearTimeout(leaveTimer.current);
  }, []);

  const triggerProps: Record<string, unknown> = { ref: triggerRef };
  if (trigger.includes('hover')) {
    triggerProps.onMouseEnter = show;
    triggerProps.onMouseLeave = hide;
    triggerProps.onFocus = show;
    triggerProps.onBlur = hide;
  }
  if (trigger.includes('click')) {
    triggerProps.onClick = () => setOpen(!isOpen);
  }
  if (trigger.includes('contextMenu')) {
    triggerProps.onContextMenu = (e: React.MouseEvent) => {
      e.preventDefault();
      setContextPos({ x: e.clientX, y: e.clientY });
      setOpen(true);
    };
  }

  const handleSelect = (key: string) => {
    menu.onClick?.(key);
    setOpen(false);
  };

  // Compute the width for matchTriggerWidth — runtime value
  const triggerWidth = matchTriggerWidth && triggerRef.current ? triggerRef.current.offsetWidth : undefined;

  return (
    <>
      {React.cloneElement(children, triggerProps)}
      <AnimatePresence>
        {isOpen && (
          <Portal>
            <motion.div
              ref={floatingRef}
              role="menu"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: duration.normal / 1000 }}
              className={clsx(styles.menu)}
              style={{
                '--dropdown-top': `${resolvedPosition.top}px`,
                '--dropdown-left': `${resolvedPosition.left}px`,
                '--dropdown-z': zIndex,
                ...(triggerWidth !== undefined && { '--dropdown-width': `${triggerWidth}px` }),
                ...customMenuStyle,
              } as React.CSSProperties}
              onMouseEnter={trigger.includes('hover') ? () => clearTimeout(leaveTimer.current) : undefined}
              onMouseLeave={trigger.includes('hover') ? hide : undefined}
            >
              {menu.items.map((item, i) => {
                const node = <DropdownItem item={item} onSelect={handleSelect} />;
                // Skip motion wrapper when reduced-motion is requested.
                if (shouldReduce) return <React.Fragment key={item.key ?? i}>{node}</React.Fragment>;
                return (
                  <motion.div key={item.key ?? i} {...staggerItem(i)}>
                    {node}
                  </motion.div>
                );
              })}
            </motion.div>
          </Portal>
        )}
      </AnimatePresence>
    </>
  );
}
